import { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export function FilterSelect({
  label,
  value,
  options,
  onChange,
  multiple = false,
}: {
  multiple?: boolean;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false),
    [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null),
    button = useRef<HTMLButtonElement>(null),
    typed = useRef({ text: '', time: 0 });
  const id = useId();
  const values = multiple ? value.split(',').filter(Boolean) : [value];
  const selected = Math.max(
    0,
    options.findIndex((option) => values.includes(option.value)),
  );
  const show = () => {
    setActive(selected);
    setOpen(true);
  };
  const choose = (index: number) => {
    if (options[index]) onChange(options[index].value);
    if (!multiple) setOpen(false);
    button.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);
  useEffect(() => {
    if (open) document.getElementById(id + '-' + active)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);
  return (
    <div
      className="filter-select"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <span id={id + '-label'} className="filter-label">
        {label}
      </span>
      <button
        type="button"
        ref={button}
        className="filter-trigger"
        role="combobox"
        aria-label={label === 'Sort by' ? label : 'Filter by ' + label.toLowerCase()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-activedescendant={open ? id + '-' + active : undefined}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            return;
          }
          if (event.key === 'Tab') {
            setOpen(false);
            return;
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open ? choose(active) : show();
            return;
          }
          if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            if (!open) {
              show();
              return;
            }
            setActive((index) =>
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? options.length - 1
                  : Math.max(
                      0,
                      Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)),
                    ),
            );
            return;
          }
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const now = Date.now();
            typed.current = {
              text:
                (now - typed.current.time < 600 ? typed.current.text : '') +
                event.key.toLowerCase(),
              time: now,
            };
            const match = options.findIndex((option) =>
              option.label.toLowerCase().startsWith(typed.current.text),
            );
            if (match >= 0) {
              setActive(match);
              setOpen(true);
            }
          }
        }}
      >
        <span>
          {multiple
            ? values.length === 0
              ? 'All brands'
              : values.length === 1
                ? options.find((o) => o.value === values[0])?.label
                : values.length + ' brands'
            : options[selected]?.label}
        </span>
        <ChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <div
          className="filter-menu"
          id={id}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          aria-labelledby={id + '-label'}
        >
          {options.map((option, index) => (
            <div
              id={id + '-' + index}
              key={option.value}
              role="option"
              aria-selected={values.includes(option.value)}
              className={'filter-option' + (index === active ? ' is-active' : '')}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(index)}
            >
              <span>{option.label}</span>
              {values.includes(option.value) && <Check size={14} aria-hidden />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
